import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconPlus, IconSend } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { CompetencyMap, CreateGoalData, Goal, User } from '../../types';
import { useCreateGoal, useReviewRequests, useUserGoals } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';
import { getGoalStatusColor, getGoalStatusText } from '../../utils/statusUtils';
import { GoalModal } from './GoalModal';
import { CompetencyDrawer } from '../competency/CompetencyDrawer';

interface Props {
  user: User;
  competencyMap: CompetencyMap;
  isOwnProfile: boolean;
}

/**
 * План развития: компетенции следующего грейда и цели под них. Цель ставит тот,
 * кто видит цели человека (сам, лид, админ); после выполненной цели сам
 * сотрудник подаёт компетенцию на проверку.
 */
export const DevelopmentPlanTab: React.FC<Props> = ({ user, competencyMap, isOwnProfile }) => {
  const { t } = useTranslation();
  const { data: goals = [], isLoading: isGoalsLoading, error: goalsError } = useUserGoals(user._id);
  // Свои заявки на проверку: поданную компетенцию не предлагаем подать ещё раз.
  // Тот же запрос, что у карточки компетенции, — кэш общий.
  const { data: pendingRequests = [] } = useReviewRequests(
    { applicant: user._id, status: 'pending' },
    isOwnProfile,
  );
  const createGoal = useCreateGoal();
  const [goalFor, setGoalFor] = useState<string | null>(null);
  const [drawerCompId, setDrawerCompId] = useState<string | null>(null);

  const gradeIdx = competencyMap.grades.findIndex((g) => g._id === user.grade?._id);
  const nextGrade = gradeIdx >= 0 ? competencyMap.grades[gradeIdx + 1] : undefined;
  const competencies = useMemo(() => nextGrade?.competencies ?? [], [nextGrade]);
  const acquired = useMemo(
    () => new Set((user.competencies ?? []).map((c) => c.competencyId)),
    [user.competencies],
  );
  const goalsByCompetency = useMemo(() => {
    const map = new Map<string, Goal[]>();
    for (const g of goals) {
      for (const c of g.competencies ?? []) map.set(c._id, [...(map.get(c._id) ?? []), g]);
    }
    return map;
  }, [goals]);
  // Стабильный массив: новый на каждом рендере сбрасывал бы форму цели в GoalModal
  // (он в зависимостях её эффекта) — например, пока идёт сохранение.
  const initialCompetencyIds = useMemo(() => (goalFor ? [goalFor] : undefined), [goalFor]);

  if (goalsError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title={t('messages.error')} color="red">
        {t('messages.errorLoading', { item: t('goals.title') })}
      </Alert>
    );
  }
  if (isGoalsLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!nextGrade || competencies.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        {t('profile.plan.noNext')}
      </Text>
    );
  }

  const nextIds = new Set(competencies.map((c) => c._id));
  const planGoals = goals.filter((g) => g.competencies?.some((c) => nextIds.has(c._id)));
  const doneGoals = planGoals.filter((g) => g.status === 'completed').length;
  const acquiredCount = competencies.filter((c) => acquired.has(c._id)).length;
  // Недостающие — сверху: план про то, что ещё предстоит.
  const rows = [...competencies].sort(
    (a, b) => Number(acquired.has(a._id)) - Number(acquired.has(b._id)),
  );
  const missingOptions = competencies
    .filter((c) => !acquired.has(c._id))
    .map((c) => ({ value: c._id, label: c.title }));
  const drawerComp = competencies.find((c) => c._id === drawerCompId);

  const handleCreate = async (data: CreateGoalData) => {
    try {
      await createGoal.mutateAsync(data);
      showToast({ title: t('messages.created', { item: t('goals.title') }), message: '' });
      setGoalFor(null);
    } catch {
      showToast({
        title: t('messages.error'),
        message: t('messages.errorSaving', { item: t('goals.title') }),
        type: 'error',
      });
    }
  };

  return (
    <Stack gap="md">
      <Stack gap={6}>
        <Title order={4}>{t('profile.plan.title', { grade: nextGrade.title })}</Title>
        <Progress
          value={(acquiredCount / competencies.length) * 100}
          color="teal"
          size="sm"
          radius="xl"
        />
        <Group gap="lg">
          <Text size="sm" c="dimmed">
            {t('profile.plan.competencies', {
              acquired: acquiredCount,
              total: competencies.length,
            })}
          </Text>
          <Text size="sm" c="dimmed">
            {t('profile.plan.goals', { done: doneGoals, total: planGoals.length })}
          </Text>
        </Group>
      </Stack>

      {rows.map((c) => {
        const isAcquired = acquired.has(c._id);
        const linked = goalsByCompetency.get(c._id) ?? [];
        const hasActiveGoal = linked.some((g) => g.status !== 'completed');
        const hasDoneGoal = linked.some((g) => g.status === 'completed');
        const isPendingReview = pendingRequests.some(
          (r) => r.competency?._id === c._id && r.competencyMap?._id === competencyMap._id,
        );
        return (
          <Paper key={c._id} withBorder p="sm" radius="md">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" fw={500}>
                    {c.title}
                  </Text>
                  <Badge size="sm" variant="light" color={isAcquired ? 'teal' : 'gray'}>
                    {isAcquired ? t('profile.plan.acquired') : t('profile.plan.missing')}
                  </Badge>
                </Group>
                {linked.map((g) => (
                  <Group key={g._id} gap={6} wrap="nowrap">
                    {/* Статус не сжимается: на 375px длинное название съедало его целиком. */}
                    <Badge
                      size="xs"
                      variant="dot"
                      color={getGoalStatusColor(g)}
                      style={{ flexShrink: 0 }}
                    >
                      {getGoalStatusText(g, t)}
                    </Badge>
                    <Text size="xs" c="dimmed" truncate style={{ minWidth: 0 }}>
                      {g.title}
                    </Text>
                  </Group>
                ))}
              </Stack>
              {!isAcquired && isOwnProfile && isPendingReview && (
                <Badge size="sm" variant="light" color="yellow">
                  {t('profile.plan.pending')}
                </Badge>
              )}
              {!isAcquired && isOwnProfile && hasDoneGoal && !isPendingReview && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconSend size={14} />}
                  onClick={() => setDrawerCompId(c._id)}
                >
                  {t('profile.plan.submit')}
                </Button>
              )}
              {!isAcquired && !hasActiveGoal && !hasDoneGoal && (
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setGoalFor(c._id)}
                >
                  {t('profile.plan.setGoal')}
                </Button>
              )}
            </Group>
          </Paper>
        );
      })}

      <GoalModal
        opened={!!goalFor}
        onClose={() => setGoalFor(null)}
        onSubmit={(data) => handleCreate(data as CreateGoalData)}
        userId={user._id}
        isLoading={createGoal.isPending}
        competencyOptions={missingOptions}
        initialCompetencyIds={initialCompetencyIds}
      />
      <CompetencyDrawer
        drawer={
          drawerComp
            ? {
                comp: drawerComp,
                gradeIdx: gradeIdx + 1,
                // Свимлейн мог исчезнуть из карты — карточке нужен хоть какой-то.
                swimlane: competencyMap.swimlanes.find((s) => s._id === drawerComp.swimlaneId) ?? {
                  _id: drawerComp.swimlaneId,
                  title: '',
                },
              }
            : null
        }
        onClose={() => setDrawerCompId(null)}
        grades={competencyMap.grades.map((g) => g.title)}
        currentUser={user}
        userCompetencies={user.competencies || []}
        competencyMapId={competencyMap._id}
      />
    </Stack>
  );
};
